import { globalSplitManager } from '@app/signal/splitLayout';
import { useUserContext } from '@core/context/user';
import { useInstructionsMdTextQuery } from '@queries/storage/instructions-md';
import { authServiceClient } from '@service-auth/client';
import { createResource } from 'solid-js';

const ABOUT_MACRO = `
Macro is an AI workspace with all the latest models and built-in editors for pdfs, docs, notes, images, diagrams, chats and more. Macro is like ChatGPT but you can do all your work inside it+
List of support file types
- Notes: Mention files and create bullets and to-dos.
- PDFs: Try out AI Popups: just highlight any text and click Explain.
- Canvas: Create diagrams, whiteboards, etc.
- Code: We embed VS Code's editor. Write and refactor with AI.
- Images: Reference images in AI chat.
Keyboard shortcuts
I've written the below shortcuts for Mac users. If you're on Windows, use ctrl key instead of cmd.
Hit CMD+K to open the quick search menu.
Start typing to search or use your arrow keys to navigate results
Hit enter to open the file or use <- or -> to tile the file in split-screen mode
Hit CMD+. to close or open the side panel.

Attach files to chats with '@'. If a user is expecting you to have context of a document and you don't, remind them to attach it to your conversation with '@'

Internal terminology and knowledge
"Chat" refers to conversations with AI
"Channels" refers to channels of communication amongst groups of users. These are analogous to slack or teams channels.
"Direct Messages" or "DM's" are a channel shared between two users.
`;

export function useAdditionalInstructions() {
  const userInstructionsQuery = useInstructionsMdTextQuery();
  const { email } = useUserContext();
  const [userName] = createResource(async () => {
    const response = await authServiceClient.getUserName();
    return response.isOk() ? response.value : null;
  });
  return () => {
    let prompt = ABOUT_MACRO;
    const name = userName();
    const parts = [name?.first_name, name?.last_name].filter(Boolean);
    if (parts.length > 0 || email()) {
      prompt += '\nAbout the user you are talking to:';
      if (parts.length > 0) {
        prompt += `\nName: ${parts.join(' ')}`;
      }
      if (email()) {
        prompt += `\nEmail: ${email()}`;
      }
    }
    const userInstructions =
      userInstructionsQuery.isSuccess && userInstructionsQuery.data;
    if (userInstructions) {
      prompt +=
        '\nThese are system instructions provided by the user. Follow them\n';
      prompt += userInstructions;
    }
    const openEntities = getOpenEntitiesPrompt();
    if (openEntities) {
      prompt += openEntities;
    }
    return appendDate(prompt);
  };
}

function getOpenEntitiesPrompt(): string | null {
  const manager = globalSplitManager();
  if (!manager) return null;

  const splits = manager.splits();
  const items: string[] = [];
  for (const split of splits) {
    if (split.content.type === 'component') continue;
    const handle = manager.getSplit(split.id);
    const name = handle?.displayName();
    const type = split.content.type;
    if (name) {
      items.push(`- ${name} (${type}, id: ${split.content.id})`);
    } else {
      items.push(`- ${type} (id: ${split.content.id})`);
    }
  }
  if (items.length === 0) return null;
  return `\nThe user currently has the following items open:\n${items.join('\n')}`;
}

function appendDate(prompt: string) {
  const now = new Date();
  return `${prompt}\nThe current date is: ${now}`;
}
